import { useState } from "react";
import { toast } from "sonner";
import { Lock, LockOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Confirm } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, periodLabel } from "@/lib/format";

function UnlockDialog({ open, onOpenChange, period, onDone }) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    try {
      const { data } = await api.post("/recaps/lock", { period, locked: false, reason });
      toast.success("Kunci rekap dibuka & tercatat di audit log");
      setReason("");
      onOpenChange(false);
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Buka Kunci Rekap {periodLabel(period)}</DialogTitle>
          <DialogDescription>Invoice yang sudah disahkan tidak ikut berubah. Rekap akan terkunci lagi otomatis saat ada invoice disahkan berikutnya.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} placeholder="Alasan membuka kunci (wajib, min. 5 karakter)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="recap-unlock-reason" />
        <DialogFooter><Button onClick={submit} disabled={reason.trim().length < 5} className="bg-amber-600 hover:bg-amber-700" data-testid="recap-unlock-submit">Buka Kunci</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PeriodStageBar({ lock, onChange }) {
  const { can } = useAuth();
  const steps = ["draft", "diperiksa", "ditutup"];
  const idx = steps.indexOf(lock.stage || "draft");
  const mark = async (reviewed) => {
    try { onChange((await api.post("/recaps/review", { period: lock.period, reviewed })).data); toast.success(reviewed ? "Periode ditandai DIPERIKSA" : "Periode kembali DRAFT"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="period-stage-bar">
      <span className="text-slate-600">Status periode:</span>
      {steps.map((s, i) => (
        <span key={s} className={`px-2 py-0.5 rounded-sm border font-semibold ${i === idx ? "bg-emerald-700 text-white border-emerald-700" : i < idx ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-slate-500 border-slate-300"}`} data-testid={`period-stage-${s}`}>{i + 1}. {s.toUpperCase()}{i === idx ? " ← sekarang" : ""}</span>
      ))}
      {lock.stage === "diperiksa" && lock.reviewed_by && <span className="text-slate-500">diperiksa {lock.reviewed_by} · {fmtDateTime(lock.reviewed_at)}</span>}
      {can("invoice:approve") && lock.stage === "draft" && (
        <Confirm title="Tandai periode DIPERIKSA?" description="Menandakan rekap sudah diperiksa. Jika rekap diubah lagi, status otomatis kembali DRAFT. Periode DITUTUP otomatis saat invoice difinalisasi." onConfirm={() => mark(true)} testId="period-review"
          trigger={<Button size="sm" variant="outline" className="h-9 sm:h-7" data-testid="period-review-btn">Tandai Diperiksa</Button>} />
      )}
      {can("invoice:approve") && lock.stage === "diperiksa" && <Button size="sm" variant="ghost" className="h-7" onClick={() => mark(false)} data-testid="period-unreview-btn">Kembalikan ke Draft</Button>}
    </div>
  );
}

export function RecapLockBanner({ lock, onChange }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const relock = async () => {
    try { onChange((await api.post("/recaps/lock", { period: lock.period, locked: true, reason: "Dikunci manual" })).data); toast.success("Rekap dikunci"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (lock.locked) {
    return (
      <div className="flex flex-wrap items-center gap-3 border border-slate-400 bg-slate-100 rounded-sm px-4 py-3" data-testid="recap-lock-banner">
        <Lock className="h-5 w-5 text-slate-700 shrink-0" />
        <div className="flex-1 min-w-[200px] text-sm">
          <div className="font-semibold text-slate-900">Rekap {periodLabel(lock.period)} terkunci</div>
          <div className="text-slate-600 text-xs">{lock.approved_count} invoice sudah disahkan. Angka rekap tidak dapat diubah atau diimpor.{lock.by ? ` · ${lock.reason} (${lock.by}, ${fmtDateTime(lock.at)})` : ""}</div>
        </div>
        {can("recap:unlock") && <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="recap-unlock-btn"><LockOpen className="h-4 w-4 mr-2" />Buka Kunci</Button>}
        <UnlockDialog open={open} onOpenChange={setOpen} period={lock.period} onDone={onChange} />
      </div>
    );
  }
  if (!lock.approved_count) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 border border-amber-300 bg-amber-50 rounded-sm px-4 py-3" data-testid="recap-unlocked-banner">
      <LockOpen className="h-5 w-5 text-amber-700 shrink-0" />
      <div className="flex-1 min-w-[200px] text-sm">
        <div className="font-semibold text-amber-900">Kunci dibuka sementara</div>
        <div className="text-amber-800 text-xs">Oleh {lock.by} · {fmtDateTime(lock.at)} · Alasan: {lock.reason}. Perubahan tidak mengubah invoice yang sudah disahkan.</div>
      </div>
      {can("recap:unlock") && <Button variant="outline" size="sm" onClick={relock} data-testid="recap-relock-btn"><Lock className="h-4 w-4 mr-2" />Kunci Kembali</Button>}
    </div>
  );
}
